import { useState, useMemo, useCallback } from "react";
import type { GeoTypeSelectionProps, GeoTypeSelectionViewModel } from "./GeoTypeSelection.types";
import type { GeoType } from "../../WizardContainer";
import { createJobsFromWizard } from "./createJobsFromWizard";
import { Oops } from "../../../../../../../utilities/oops";
import { toast } from "react-toastify";

/**
 * Checks if a geo-type represents a boundary
 */
export function isBoundaryGeoType(geoType: GeoType | null): boolean {
	return geoType === "Boundary";
}

/**
 * Gets the geometry type that a geo-type applies to
 */
export function getGeometryTypeForGeoType(geoType: GeoType): "polygon" | "point" {
	return geoType === "Boundary" || geoType === "Zones" ? "polygon" : "point";
}

/**
 * Hook for GeoTypeSelection step logic
 */
export function useGeoTypeSelectionViewModel(props: GeoTypeSelectionProps): GeoTypeSelectionViewModel {
	const {
		geodataGroups,
		geoTypeSelections,
		excludedGroupIds,
		selectedGroupIdsForMap,
		onGeoTypeChange,
		onApplyToAll,
		onApplyToSelected,
		onExcludeGroup,
		onIncludeGroup,
		onMapSelectionChange,
		onPrevious,
		onNext,
	} = props;

	// State for job creation progress
	const [isCreatingJobs, setIsCreatingJobs] = useState(false);

	// Computed: Active groups (non-excluded), sorted with polygons before points
	const activeGroups = useMemo(
		() =>
			geodataGroups
				.filter((g) => !excludedGroupIds.has(g.id))
				.sort((a, b) => {
					if (a.geometryType === b.geometryType) return 0;
					return a.geometryType === "polygon" ? -1 : 1;
				}),
		[geodataGroups, excludedGroupIds],
	);

	// Computed: Excluded groups
	const excludedGroups = useMemo(
		() => geodataGroups.filter((g) => excludedGroupIds.has(g.id)),
		[geodataGroups, excludedGroupIds],
	);

	// Computed: Polygon groups (active only)
	const polygonGroups = useMemo(() => activeGroups.filter((g) => g.geometryType === "polygon"), [activeGroups]);

	// Computed: Point groups (active only)
	const pointGroups = useMemo(() => activeGroups.filter((g) => g.geometryType === "point"), [activeGroups]);

	// Computed: Whether any groups are selected for map
	const hasMapSelection = useMemo(() => selectedGroupIdsForMap.length > 0, [selectedGroupIdsForMap]);

	// Computed: Geometry type of currently selected groups
	const selectedGeometryType = useMemo((): "polygon" | "point" | null => {
		if (selectedGroupIdsForMap.length === 0) return null;

		const selectedGroups = activeGroups.filter((g) => selectedGroupIdsForMap.includes(g.id));
		if (selectedGroups.length === 0) return null;

		const types = new Set(selectedGroups.map((g) => g.geometryType));
		if (types.size === 1) {
			return selectedGroups[0].geometryType;
		}
		return null; // Mixed types
	}, [activeGroups, selectedGroupIdsForMap]);

	// Computed: Selected polygon/point counts
	const selectedPolygonCount = useMemo(
		() => activeGroups.filter((g) => selectedGroupIdsForMap.includes(g.id) && g.geometryType === "polygon").length,
		[activeGroups, selectedGroupIdsForMap],
	);
	const selectedPointCount = useMemo(
		() => activeGroups.filter((g) => selectedGroupIdsForMap.includes(g.id) && g.geometryType === "point").length,
		[activeGroups, selectedGroupIdsForMap],
	);

	// Computed: Total counts
	const totalActiveGroups = useMemo(() => activeGroups.length, [activeGroups]);
	const totalExcludedGroups = useMemo(() => excludedGroups.length, [excludedGroups]);

	// Computed: Assignment counts
	const assignedCount = useMemo(
		() => activeGroups.filter((g) => geoTypeSelections[g.id] != null).length,
		[activeGroups, geoTypeSelections],
	);
	const unassignedCount = useMemo(() => totalActiveGroups - assignedCount, [totalActiveGroups, assignedCount]);

	// Computed: Whether there is at least one boundary group
	const hasBoundaryGroup = useMemo(
		() => activeGroups.some((g) => geoTypeSelections[g.id] === "Boundary"),
		[activeGroups, geoTypeSelections],
	);

	// Computed: Can proceed validation
	const canProceed = useMemo(() => {
		// All active groups must have geo-types assigned
		const allAssigned = activeGroups.every((g) => geoTypeSelections[g.id] != null);
		if (!allAssigned) return false;

		// At least one boundary must exist
		if (!hasBoundaryGroup) return false;

		// Must have at least one active group
		if (activeGroups.length === 0) return false;

		return true;
	}, [activeGroups, geoTypeSelections, hasBoundaryGroup]);

	// Computed: Reason for not being able to proceed
	const cannotProceedReason = useMemo((): string | null => {
		if (activeGroups.length === 0) {
			return "No active groups. Include at least one group.";
		}
		if (unassignedCount > 0) {
			return `${unassignedCount} group${unassignedCount > 1 ? "s" : ""} still need a geo-type assigned.`;
		}
		if (!hasBoundaryGroup) {
			return "At least one group must be assigned as Boundaries.";
		}
		return null;
	}, [activeGroups.length, unassignedCount, hasBoundaryGroup]);

	// Handler: Geo-type change for single group
	const handleGeoTypeChange = useCallback(
		(groupId: string, geoType: GeoType | null) => {
			onGeoTypeChange(groupId, geoType);
		},
		[onGeoTypeChange],
	);

	// Handler: Apply to all polygons
	const handleApplyToAllPolygons = useCallback(
		(geoType: GeoType) => {
			onApplyToAll("polygon", geoType);
		},
		[onApplyToAll],
	);

	// Handler: Apply to all points
	const handleApplyToAllPoints = useCallback(
		(geoType: GeoType) => {
			onApplyToAll("point", geoType);
		},
		[onApplyToAll],
	);

	// Handler: Apply to selected
	const handleApplyToSelected = useCallback(
		(geoType: GeoType) => {
			onApplyToSelected(geoType);
		},
		[onApplyToSelected],
	);

	// Handler: Toggle map selection
	const handleToggleMapSelection = useCallback(
		(groupId: string) => {
			const group = activeGroups.find((g) => g.id === groupId);
			if (!group) return;

			const isCurrentlySelected = selectedGroupIdsForMap.includes(groupId);

			if (isCurrentlySelected) {
				// Deselect
				onMapSelectionChange(selectedGroupIdsForMap.filter((id) => id !== groupId));
			} else {
				// Select - always add to selection (mixed point+polygon allowed)
				onMapSelectionChange([...selectedGroupIdsForMap, groupId]);
			}
		},
		[activeGroups, selectedGroupIdsForMap, onMapSelectionChange],
	);

	// Handler: Select all for map (all active groups regardless of geometry type)
	const handleSelectAllForMap = useCallback(() => {
		onMapSelectionChange(activeGroups.map((g) => g.id));
	}, [activeGroups, onMapSelectionChange]);

	// Handler: Deselect all for map
	const handleDeselectAllForMap = useCallback(() => {
		onMapSelectionChange([]);
	}, [onMapSelectionChange]);

	// Handler: Exclude group
	const handleExcludeGroup = useCallback(
		(groupId: string) => {
			// Also remove from map selection if selected
			if (selectedGroupIdsForMap.includes(groupId)) {
				onMapSelectionChange(selectedGroupIdsForMap.filter((id) => id !== groupId));
			}
			onExcludeGroup(groupId);
		},
		[onExcludeGroup, selectedGroupIdsForMap, onMapSelectionChange],
	);

	// Handler: Include group
	const handleIncludeGroup = useCallback(
		(groupId: string) => {
			onIncludeGroup(groupId);
		},
		[onIncludeGroup],
	);

	// Handler: Previous button
	const handlePrevious = useCallback(() => {
		onPrevious();
	}, [onPrevious]);

	// Handler: Next button (triggers job creation)
	const handleNext = useCallback(async () => {
		if (!canProceed || isCreatingJobs) return;

		setIsCreatingJobs(true);
		try {
			// Create jobs from the wizard configuration
			const jobs = await createJobsFromWizard({
				geodataGroups,
				geoTypeSelections,
				excludedGroupIds,
				propertyMappings: props.propertyMappings,
				configuration: props.configuration,
				company: props.company,
				allDeals: props.allDeals,
				salespersons: props.salespersons,
				branches: props.branches,
				growers: props.growers,
				user: props.user,
			});

			if (jobs.length === 0) {
				toast.error("No jobs could be created. Please check your geo-type assignments.");
				return;
			}

			await onNext(jobs);
		} catch (error) {
			Oops.handleError(error, "GeoTypeSelectionStep_HandleNext");
		} finally {
			setIsCreatingJobs(false);
		}
	}, [canProceed, isCreatingJobs, geodataGroups, geoTypeSelections, excludedGroupIds, props, onNext]);

	return {
		activeGroups,
		excludedGroups,
		polygonGroups,
		pointGroups,
		hasMapSelection,
		selectedGeometryType,
		selectedPolygonCount,
		selectedPointCount,
		canProceed,
		cannotProceedReason,
		isCreatingJobs,
		totalActiveGroups,
		totalExcludedGroups,
		assignedCount,
		unassignedCount,
		hasBoundaryGroup,
		handleGeoTypeChange,
		handleApplyToAllPolygons,
		handleApplyToAllPoints,
		handleApplyToSelected,
		handleToggleMapSelection,
		handleSelectAllForMap,
		handleDeselectAllForMap,
		handleExcludeGroup,
		handleIncludeGroup,
		handlePrevious,
		handleNext,
	};
}
